"use client"

import { LogOut, User } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/use-auth"

interface NavbarProps {
  className?: string
  transparent?: boolean
}

export function Navbar({ className, transparent }: NavbarProps) {
  const { isAuthenticated, email, isLoading, logout } = useAuth()

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50",
        transparent
          ? "bg-[var(--brand-white-hills)]/80 backdrop-blur-md"
          : "border-b border-[var(--border-subtle)] bg-[var(--bg-page)]/90 backdrop-blur-md",
        className
      )}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 sm:px-10">
        <Link href="/" className="flex items-center gap-2.5">
          <Image
            src="/brand/Logo-Star_dark.svg"
            alt=""
            width={28}
            height={28}
            className="h-7 w-7"
          />
          <span
            className="text-sm font-medium tracking-[0.25em] uppercase"
            style={{ color: "var(--brand-night-sky)" }}
          >
            Lumensis
          </span>
        </Link>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {isLoading ? (
            <div className="h-8 w-20 animate-pulse rounded-[var(--radius-sm)] bg-[var(--bg-elevated)]" />
          ) : isAuthenticated ? (
            <>
              <Link
                href="/upload"
                className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
              >
                Analyze
              </Link>
              <Link
                href="/history"
                className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
              >
                Results
              </Link>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-white px-3 py-1.5">
                  <User className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                  <span className="max-w-[140px] truncate text-xs text-[var(--text-secondary)]">
                    {email || "Account"}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-xs text-[var(--text-muted)] transition-colors hover:text-[var(--color-danger)]"
                  title="Sign out"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className="px-3 py-1.5 text-sm font-medium transition-colors"
                style={{ color: "var(--brand-night-sky)" }}
              >
                sign in
              </Link>
              <Link
                href="/login"
                className="rounded-full px-5 py-2 text-sm font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: "var(--brand-night-sky)",
                  color: "var(--brand-sun)",
                }}
              >
                See your mind
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}
